import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { davidCandidateApi, ChatResponseV2 } from '../../services/davidCandidateApi';
import { useCandidateData } from '../../hooks/useCandidateData';
import * as onboardingApi from '../../../shared/welcome/services/onboardingApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Search,
  Send,
  Paperclip,
  ArrowUpRight,
  ArrowLeft,
  Bookmark,
  UserCircle,
  BrainCircuit
} from 'lucide-react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'david';
  timestamp: Date;
  isTyping?: boolean;
  attachment?: {
    name: string;
    size: number;
    type: string;
  };
}

interface TalkToDavidProps {
  mode?: 'chat' | 'onboarding';
  supabaseUserId?: string;
}

/**
 * Talk to David - AI Assistant Chat Component
 * Redesigned with modern UI matching the provided design
 */
const TalkToDavid: React.FC<TalkToDavidProps> = ({ mode = 'chat', supabaseUserId: propUserId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { supabaseUserId?: string; mode?: 'onboarding' } | undefined;
  const actualMode = state?.mode || mode;
  const actualUserId = state?.supabaseUserId || propUserId;

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [chatSessionId, setChatSessionId] = useState(() => davidCandidateApi.generateSessionId());
  const [isCompleted, setIsCompleted] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [hasReturnedToWelcome, setHasReturnedToWelcome] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const preventNavigationRef = useRef(false);

  // Onboarding specific state
  const [onboardingStatus, setOnboardingStatus] = useState<onboardingApi.OnboardingStatus | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [remainingQuestions, setRemainingQuestions] = useState<onboardingApi.MissingContentQuestion[]>([]);

  const { profile, isLoading: isProfileLoading, error: profileError } = useCandidateData();

  useEffect(() => {
    console.log('[TalkToDavid] Profile loading status:', isProfileLoading);
    console.log('[TalkToDavid] Profile data:', profile);
    console.log('[TalkToDavid] Profile error:', profileError);
    console.log('[TalkToDavid] Mode:', actualMode);
    console.log('[TalkToDavid] User ID:', actualUserId);
    console.log('[TalkToDavid] showWelcome:', showWelcome);
    console.log('[TalkToDavid] hasReturnedToWelcome:', hasReturnedToWelcome);
    console.log('[TalkToDavid] preventNavigationRef:', preventNavigationRef.current);
  }, [profile, isProfileLoading, profileError, actualMode, actualUserId, showWelcome, hasReturnedToWelcome]);

  useEffect(() => {
    if (actualMode === 'onboarding' && !hasReturnedToWelcome) {
      loadOnboardingQuestions();
    }
  }, [actualMode, hasReturnedToWelcome]);

  const loadOnboardingQuestions = async () => {
    const userId = actualUserId || JSON.parse(localStorage.getItem('authUser') || localStorage.getItem('user') || '{}').id;

    if (!userId) {
      console.error('[TalkToDavid] No user ID available in onboarding mode');
      const noUserMessage: Message = {
        id: `david-${Date.now()}`,
        text: "I couldn't identify your account. Let me take you to your dashboard!",
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([noUserMessage]);
      setShowWelcome(false);
      setTimeout(() => {
        if (!preventNavigationRef.current) {
          navigate('/candidate/dashboard');
        }
      }, 2000);
      return;
    }

    try {
      setIsLoading(true);
      console.log('[TalkToDavid] Loading onboarding status for user:', userId);

      const response = await onboardingApi.getOnboardingStatus(userId);
      const status = response.payload;
      setOnboardingStatus(status);

      console.log('[TalkToDavid] Onboarding status:', status);
      console.log('[TalkToDavid] Missing content questions:', status.missingQuestions);

      const pending = status.missingQuestions.filter(q => q.status === 'pending');
      setRemainingQuestions(pending);

      console.log('[TalkToDavid] Pending questions:', pending);

      if (pending.length > 0) {
        const davidMessage: Message = {
          id: `david-${Date.now()}`,
          text: pending[0].question,
          sender: 'david',
          timestamp: new Date()
        };
        setMessages([davidMessage]);
        setShowWelcome(false);
      } else {
        console.log('[TalkToDavid] No pending questions found');
        const completedMessage: Message = {
          id: `david-${Date.now()}`,
          text: "Great! You've already answered all the questions. Let me take you to your dashboard!",
          sender: 'david',
          timestamp: new Date()
        };
        setMessages([completedMessage]);
        setShowWelcome(false);
        setTimeout(() => {
          if (!preventNavigationRef.current) {
            navigate('/candidate/dashboard');
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to load onboarding questions:', error);
      const errorMessage: Message = {
        id: `david-${Date.now()}`,
        text: "I couldn't load your questions. Let me take you to your dashboard instead!",
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([errorMessage]);
      setShowWelcome(false);
      setTimeout(() => {
        if (!preventNavigationRef.current) {
          navigate('/candidate/dashboard');
        }
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const completeOnboarding = async () => {
    try {
      setIsCompleted(true);

      const completionMessage: Message = {
        id: `david-${Date.now()}`,
        text: "Great! We're all set. Your profile has been completed. Let me take you to your dashboard where you can explore job opportunities!",
        sender: 'david',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, completionMessage]);

      setTimeout(() => {
        if (!preventNavigationRef.current) {
          navigate('/candidate/dashboard');
        }
      }, 2000);
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeChat = async () => {
    try {
      setIsLoading(true);

      const response: ChatResponseV2 = await davidCandidateApi.startChatV2(chatSessionId, 'assistant');

      const davidMessage: Message = {
        id: `david-${Date.now()}`,
        text: response.message,
        sender: 'david',
        timestamp: new Date()
      };

      setMessages([davidMessage]);
    } catch (error) {
      console.error('Failed to start chat:', error);
      const fallbackMessage: Message = {
        id: `david-${Date.now()}`,
        text: 'Hi! My name is David, and I\'m excited to get to know you better.',
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      console.log('File selected:', file.name, file.size, file.type);
    }
  };

  const handleSendMessage = async () => {
    if ((inputMessage.trim() === '' && !selectedFile) || isLoading || isCompleted) return;

    const userMessageText = inputMessage.trim();

    // If on welcome screen and user typed a message, start chat and send the message
    if (showWelcome && userMessageText) {
      await startChatAndSendMessage(userMessageText);
      return;
    }

    setInputMessage('');

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: userMessageText || (selectedFile ? `[Uploaded: ${selectedFile.name}]` : ''),
      sender: 'user',
      timestamp: new Date(),
      ...(selectedFile && {
        attachment: {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type
        }
      })
    };

    setMessages(prev => [...prev, userMessage]);
    setSelectedFile(null);

    try {
      setIsLoading(true);

      if (actualMode === 'onboarding') {
        await handleOnboardingAnswer(userMessageText);
      } else {
        await handleRegularChat(userMessageText);
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);
      const errorMessage: Message = {
        id: `david-${Date.now()}`,
        text: error.response?.data?.detail || 'Sorry, I encountered an error. Please try again.',
        sender: 'david',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegularChat = async (userMessageText: string) => {
    const response: ChatResponseV2 = await davidCandidateApi.sendMessageV2(
      chatSessionId,
      userMessageText
    );

    const davidMessage: Message = {
      id: `david-${Date.now()}`,
      text: response.message,
      sender: 'david',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, davidMessage]);

    if (response.completed) {
      setIsCompleted(true);
    }
  };

  const handleOnboardingAnswer = async (answer: string) => {
    if (remainingQuestions.length === 0) return;

    const userId = actualUserId || JSON.parse(localStorage.getItem('authUser') || localStorage.getItem('user') || '{}').id;
    if (!userId) return;

    const currentQuestion = remainingQuestions[currentQuestionIndex];

    const response = await onboardingApi.answerMissingContentQuestion(
      userId,
      currentQuestion.questionId,
      answer
    );

    const result = response.payload;

    const pending = result.missingQuestions.filter(q => q.status === 'pending');
    setRemainingQuestions(pending);

    if (pending.length > 0) {
      const nextQuestion = pending[0];
      const davidMessage: Message = {
        id: `david-${Date.now()}`,
        text: nextQuestion.question,
        sender: 'david',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, davidMessage]);
      setCurrentQuestionIndex(0);
    } else {
      await completeOnboarding();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleGetStarted = async () => {
    if (!profile) {
      console.error('[TalkToDavid] Profile not loaded yet');
      return;
    }

    setShowWelcome(false);
    await initializeChat();
    textareaRef.current?.focus();
  };

  // Start chat and send the user's typed message
  const startChatAndSendMessage = async (userMessageText: string) => {
    setShowWelcome(false);
    setInputMessage('');

    try {
      setIsLoading(true);

      // Start the chat session (JWT provides candidate identity)
      const response: ChatResponseV2 = await davidCandidateApi.startChatV2(chatSessionId, 'assistant');

      // Add David's initial greeting
      const davidGreeting: Message = {
        id: `david-${Date.now()}`,
        text: response.message,
        sender: 'david',
        timestamp: new Date()
      };

      // Add user's message
      const userMessage: Message = {
        id: `user-${Date.now() + 1}`,
        text: userMessageText,
        sender: 'user',
        timestamp: new Date()
      };

      setMessages([davidGreeting, userMessage]);

      // Send the message to David and get response
      const davidResponse: ChatResponseV2 = await davidCandidateApi.sendMessageV2(
        chatSessionId,
        userMessageText
      );

      // Add David's response
      const davidMessage: Message = {
        id: `david-${Date.now() + 2}`,
        text: davidResponse.message,
        sender: 'david',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, davidMessage]);

      if (davidResponse.completed) {
        setIsCompleted(true);
      }

    } catch (error) {
      console.error('Failed to start chat and send message:', error);
      const fallbackMessage: Message = {
        id: `david-${Date.now()}`,
        text: 'Hi! My name is David, and I\'m excited to help you. Let me know more about what you\'re looking for!',
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([fallbackMessage]);
    } finally {
      setIsLoading(false);
      textareaRef.current?.focus();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleFeatureCardClick = async (featureType: string) => {
    // Hide welcome screen and initialize chat
    setShowWelcome(false);

    try {
      setIsLoading(true);

      // Start the chat session (JWT provides candidate identity)
      const response: ChatResponseV2 = await davidCandidateApi.startChatV2(chatSessionId, 'assistant');

      // Add David's initial greeting
      const davidGreeting: Message = {
        id: `david-${Date.now()}`,
        text: response.message,
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([davidGreeting]);

      // Generate the appropriate prompt based on feature type
      let userPrompt = '';
      switch (featureType) {
        case 'jobs':
          userPrompt = 'Find me matching jobs based on my profile and preferences.';
          break;
        case 'saved_jobs':
          userPrompt = 'Show me my saved jobs.';
          break;
        case 'profile':
          userPrompt = 'Show me my profile.';
          break;
        case 'career':
          userPrompt = 'I need help with career planning. Can you give me personalized career advice and help me plan my next steps?';
          break;
      }

      // Add user's automated message
      const userMessage: Message = {
        id: `user-${Date.now() + 1}`,
        text: userPrompt,
        sender: 'user',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);

      // Send the message to David via v2 and get response
      const davidResponse: ChatResponseV2 = await davidCandidateApi.sendMessageV2(
        chatSessionId,
        userPrompt
      );

      // Add David's contextual response
      const davidMessage: Message = {
        id: `david-${Date.now() + 2}`,
        text: davidResponse.message,
        sender: 'david',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, davidMessage]);

      if (davidResponse.completed) {
        setIsCompleted(true);
      }

    } catch (error) {
      console.error('Failed to start feature chat:', error);
      const fallbackMessage: Message = {
        id: `david-${Date.now()}`,
        text: 'Hi! My name is David, and I\'m excited to help you with that. Let me know more about what you\'re looking for!',
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([fallbackMessage]);
    } finally {
      setIsLoading(false);
      textareaRef.current?.focus();
    }
  };

  const FeatureCard = ({ color, icon: Icon, title, desc, featureType }: any) => (
    <button
      onClick={() => handleFeatureCardClick(featureType)}
      disabled={isProfileLoading || !profile || !!profileError}
      className={`flex flex-col justify-between p-6 rounded-3xl h-[200px] w-full text-white transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${color}`}
    >
      <div className="flex flex-col gap-4">
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
          <Icon size={20} className="text-white" />
        </div>
        <h3 className="text-xl font-bold leading-tight text-left">{title}</h3>
      </div>
      <div className="flex justify-between items-end">
        <p className="text-[10px] text-white/80 text-left max-w-[80%]">{desc}</p>
        <ArrowUpRight size={20} />
      </div>
    </button>
  );

  return (
    <div className="flex flex-col h-full font-manrope bg-[#F8F9FB]">
      {showWelcome ? (
        /* Welcome Screen */
        <div className="flex flex-col h-[calc(100vh-64px)] animate-fade-in items-center justify-center relative">
          <div className="w-full max-w-5xl mx-auto px-4 flex flex-col items-center justify-center flex-1">
            {/* Hero Text */}
            <div className="relative mb-16 text-center">
              <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-64 h-64 bg-[#CFE3FF] rounded-full blur-3xl opacity-50 -z-10"></div>
              <h1 className="text-5xl md:text-6xl font-bold text-[#015FA6] mb-2 tracking-tight">
                Hi! My Name Is David,
              </h1>
              <h2 className="text-4xl md:text-5xl font-medium text-[#A5A5A5] tracking-tight">
                And I'm Excited To Get To <br /> Know You Better.
              </h2>
            </div>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full mb-16">
              <FeatureCard
                color="bg-[#015FA6]"
                icon={Search}
                title="Find matching jobs"
                desc="Instantly get roles that fit your skills & preferences."
                featureType="jobs"
              />
              <FeatureCard
                color="bg-[#FBBF24]"
                icon={Bookmark}
                title="View saved jobs"
                desc="See all the jobs you've bookmarked in one place."
                featureType="saved_jobs"
              />
              <FeatureCard
                color="bg-[#458330]"
                icon={UserCircle}
                title="View my profile"
                desc="Check how your profile looks to recruiters."
                featureType="profile"
              />
              <FeatureCard
                color="bg-black"
                icon={BrainCircuit}
                title="Career advice"
                desc="Get personalized career guidance and next steps."
                featureType="career"
              />
            </div>

            {/* Profile Error Message */}
            {profileError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 max-w-2xl">
                <p className="font-medium">Profile Error</p>
                <p className="text-sm">{profileError}</p>
              </div>
            )}
          </div>

          {/* Chat Input Area */}
          <div className="w-full max-w-4xl mx-auto px-4 pb-8">
            <div className="relative bg-white rounded-2xl border border-[#EDEDED] shadow-sm p-4 flex flex-col">
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isProfileLoading ? "Loading profile..." : !profile || profileError ? "Please complete your profile first" : "Type your message here..."}
                disabled={isLoading || isProfileLoading || !profile || !!profileError}
                className="w-full min-h-[60px] resize-none outline-none text-base placeholder-[#A5A5A5] text-[#0B111E] disabled:opacity-50"
              />

              {selectedFile && (
                <div className="flex items-center gap-2 mt-2 p-2 bg-blue-50 rounded-lg text-sm text-[#015FA6]">
                  <Paperclip size={16} />
                  <span>{selectedFile.name}</span>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="ml-auto text-gray-500 hover:text-red-500"
                  >
                    ×
                  </button>
                </div>
              )}

              <div className="flex justify-between items-center mt-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || isCompleted || isProfileLoading || !profile}
                  className="text-[#A5A5A5] hover:text-[#015FA6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Paperclip size={20} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt"
                />
                <button
                  onClick={showWelcome ? handleGetStarted : handleSendMessage}
                  disabled={isLoading || isCompleted || isProfileLoading || !profile || !!profileError}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    (inputMessage.trim() || selectedFile) && !isLoading && !isCompleted && profile && !profileError
                      ? 'bg-[#015FA6] hover:bg-[#014a85]'
                      : 'bg-[#A5A5A5]'
                  }`}
                >
                  <Send size={18} className="-ml-0.5 mt-0.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Chat Interface */
        <div className="flex flex-col h-full">
          {/* Chat Header with Back Button */}
          <div className="flex items-center gap-4 px-8 py-4 border-b border-[#EDEDED] bg-white">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('[TalkToDavid] Back button clicked - setting showWelcome to true');
                preventNavigationRef.current = true;
                setHasReturnedToWelcome(true);
                setShowWelcome(true);
                setMessages([]);
                setIsCompleted(false);
                setChatSessionId(davidCandidateApi.generateSessionId());
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Go back to welcome screen"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#015FA6] flex items-center justify-center text-white font-bold">
                D
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">David</h2>
                <p className="text-sm text-gray-500">Your AI Recruiter</p>
              </div>
            </div>
          </div>
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="max-w-4xl mx-auto space-y-6">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-2xl ${message.sender === 'user'
                    ? 'bg-[#015FA6] text-white rounded-3xl rounded-br-md px-6 py-4'
                    : 'bg-white text-gray-900 rounded-3xl rounded-bl-md px-6 py-4 border border-[#EDEDED] shadow-sm'
                    }`}>
                    {message.isTyping ? (
                      <div className="flex items-center space-x-1">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {message.sender === 'david' ? (
                          <div className="prose prose-sm max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-900 prose-p:leading-relaxed prose-strong:text-gray-900 prose-strong:font-bold prose-ul:text-gray-900 prose-ol:text-gray-900 prose-li:text-gray-900 prose-code:text-blue-600 prose-code:bg-blue-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-blockquote:border-l-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:py-1">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {message.text}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-base leading-relaxed">{message.text}</p>
                        )}
                        {message.attachment && (
                          <div className="mt-2 flex items-center gap-2 text-sm opacity-75">
                            <Paperclip size={14} />
                            <span>{message.attachment.name}</span>
                            <span className="text-xs">({(message.attachment.size / 1024).toFixed(1)} KB)</span>
                          </div>
                        )}
                        <p className={`text-xs mt-2 opacity-70 ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                          }`}>
                          {formatTime(message.timestamp)}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {/* David is thinking indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white text-gray-900 rounded-3xl rounded-bl-md px-6 py-4 border border-[#EDEDED] shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-[#015FA6] rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-[#015FA6] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-[#015FA6] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-sm text-gray-500 ml-2">David is thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="px-8 py-6 bg-[#F8F9FB]">
            <div className="max-w-4xl mx-auto">
              <div className="relative bg-white rounded-2xl border border-[#EDEDED] shadow-sm p-4 flex flex-col">
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message here..."
                  disabled={isLoading || isCompleted}
                  className="w-full min-h-[60px] resize-none outline-none text-base placeholder-[#A5A5A5] text-[#0B111E] disabled:opacity-50"
                />

                {selectedFile && (
                  <div className="flex items-center gap-2 mt-2 p-2 bg-blue-50 rounded-lg text-sm text-[#015FA6]">
                    <Paperclip size={16} />
                    <span>{selectedFile.name}</span>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="ml-auto text-gray-500 hover:text-red-500"
                    >
                      ×
                    </button>
                  </div>
                )}

                <div className="flex justify-between items-center mt-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading || isCompleted}
                    className="text-[#A5A5A5] hover:text-[#015FA6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Paperclip size={20} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={(inputMessage.trim() === '' && !selectedFile) || isLoading || isCompleted}
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${(inputMessage.trim() || selectedFile) && !isLoading && !isCompleted
                      ? 'bg-[#015FA6] hover:bg-[#014a85]'
                      : 'bg-[#A5A5A5]'
                      }`}
                  >
                    <Send size={18} className="-ml-0.5 mt-0.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700;800&display=swap');

        .font-manrope {
          font-family: 'Manrope', sans-serif;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
        }

        /* Custom Markdown styles for David's messages */
        .prose {
          color: #0B111E;
        }
        .prose h1, .prose h2, .prose h3, .prose h4 {
          margin-top: 1em;
          margin-bottom: 0.5em;
          font-weight: 700;
        }
        .prose h3 {
          font-size: 1.1em;
        }
        .prose p {
          margin-bottom: 0.75em;
          line-height: 1.6;
        }
        .prose ul, .prose ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }
        .prose li {
          margin: 0.25em 0;
        }
        .prose strong {
          font-weight: 700;
          color: #015FA6;
        }
        .prose code {
          background: #F4F7FE;
          color: #015FA6;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.9em;
          font-family: 'Monaco', 'Courier New', monospace;
        }
        .prose blockquote {
          border-left: 3px solid #015FA6;
          background: #F4F7FE;
          padding: 0.5em 1em;
          margin: 0.75em 0;
          font-style: italic;
        }
        .prose a {
          color: #015FA6;
          text-decoration: underline;
        }
        .prose a:hover {
          color: #014a85;
        }
      `}</style>
    </div>
  );
};

export default TalkToDavid;
